/**
 * Classroom game results (GamesAnswerCollector.cs → urlGamesSendResult, and
 * ProjectClassroomsShare.GetDataGames → urlGetAnswersGamesByClassroom), kept
 * in Firestore:
 *
 *   projects/{projectId}/gameResults/{resultId}
 *     { projectId, classroomId, studentId, puntuation (0–100), finished,
 *       pages, time (seconds), createdAt }
 *
 * A classroom student playing one of the classroom's games sends one record
 * when the game ends (finished) or when they close it (not finished, with the
 * pages reached). The rules only accept records from students of a classroom
 * the game is shared with, and only the project's owner can read them.
 */
import type { Slide } from "@makeit/schema";
import type { SlideResult } from "../player/context";

export interface GameResult {
  id: string;
  classroomId: string;
  studentId: string;
  puntuation: number;
  finished: boolean;
  pages: number;
  time: number;
  date: Date | null;
}

async function fs() {
  const [{ getFirestore, collection, addDoc, getDocs, query, where, serverTimestamp }, { getApp }] = await Promise.all([import("firebase/firestore"), import("../firebase")]);
  return { db: getFirestore(getApp()), collection, addDoc, getDocs, query, where, serverTimestamp };
}

/**
 * PlayerManager.GetPuntuation: 100 × Σ points / (non-free pages × 4), a page
 * giving 4 / 3 / 2 / 1 / 0 points for 0 / 1 / 2 / 3 / more errors. Pages not
 * played count as having no errors, as in the app.
 */
export function gamePuntuation(slides: Slide[], results: SlideResult[]): number {
  let pages = 0;
  let points = 0;
  for (const s of slides) {
    if (s.type === "free") continue;
    pages++;
    const errors = results.find((r) => r.slideId === s.id)?.errors ?? 0;
    points += errors <= 3 ? 4 - errors : 0;
  }
  return pages ? Math.floor((100 * points) / (pages * 4)) : 0;
}

export async function sendGameResult(r: { projectId: string; classroomId: string; studentId: string; puntuation: number; finished: boolean; pages: number; time: number }): Promise<void> {
  const { db, collection, addDoc, serverTimestamp } = await fs();
  await addDoc(collection(db, "projects", r.projectId, "gameResults"), {
    projectId: r.projectId,
    classroomId: r.classroomId,
    studentId: r.studentId,
    puntuation: Math.max(0, Math.min(100, Math.round(r.puntuation))),
    finished: r.finished,
    pages: Math.max(0, Math.min(1000, Math.round(r.pages))),
    time: Math.max(0, Math.min(86400, Math.round(r.time))),
    createdAt: serverTimestamp(),
  });
}

/** The results of one classroom for one game (the teacher's report). */
export async function fetchGameResults(projectId: string, classroomId: string): Promise<GameResult[]> {
  const { db, collection, getDocs, query, where } = await fs();
  const snap = await getDocs(query(collection(db, "projects", projectId, "gameResults"), where("classroomId", "==", classroomId)));
  return snap.docs
    .map((d) => {
      const x = d.data() as Record<string, unknown>;
      const ts = x.createdAt as { toDate?: () => Date } | null | undefined;
      return {
        id: d.id,
        classroomId: String(x.classroomId ?? ""),
        studentId: String(x.studentId ?? ""),
        puntuation: Number(x.puntuation ?? 0) || 0,
        finished: x.finished === true,
        pages: Number(x.pages ?? 0) || 0,
        time: Number(x.time ?? 0) || 0,
        date: ts?.toDate ? ts.toDate() : null,
      };
    })
    .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
}

// ---------------------------------------------------------------------------
// One student's summary (SharedUser.GetPlayedTimes / GetFinishedTimes / GetPuntuation / GetTime)
// ---------------------------------------------------------------------------

export interface StudentGameSummary {
  played: number;
  finished: number;
  /** best score of a finished game */
  best: number;
  /** quickest finished game, or the longest unfinished one when none finished */
  time: number;
}

export function summarize(results: GameResult[]): StudentGameSummary {
  const done = results.filter((r) => r.finished);
  // GetPuntuation takes the maximum over every record; unfinished ones carry the
  // score of the pages not played yet, so only finished games count here.
  const best = done.reduce((m, r) => Math.max(m, r.puntuation), 0);
  const time = done.length ? Math.min(...done.map((r) => r.time)) : results.reduce((m, r) => Math.max(m, r.time), 0);
  return { played: results.length, finished: done.length, best, time };
}

/** "mm:ss" (SharedUserAnswersGames.GetClockTime). */
export function clockTime(seconds: number): string {
  const m = Math.floor(seconds / 60) % 60;
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
